import {
  Grid,
  WithStyles,
  withStyles,
  Typography,
  CircularProgress,
  Theme
} from '@material-ui/core'
import moment, { Moment } from 'moment-timezone'
import { Fragment, useEffect, useState } from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { Dispatch } from 'redux'
import { formValueSelector } from 'redux-form'
import { closeDialog, openDialog } from '../../../../actions/dialogs'
import { OpenDialog, CloseDialog } from '../../../../actions/types'
import { selectApartmentById } from '../../../../selectors/apartments'
import {
  selectAvailabilitiesByApplicant,
  selectIsAvailabilityDeleting
} from '../../../../selectors/applicantAvailability'
import { selectContentIdFromPathname } from '../../../../selectors/router'
import { selectShowingsInterfaceByApartmentId } from '../../../../selectors/showings'
import i18n from '../../../../translations/i18n'
import { Apartment } from '../../../../types/apartments'
import { ApplicantAvailability } from '../../../../types/applicantAvailability'
import { Showing } from '../../../../types/showings'
import {
  EmptyShowingsInterface,
  StateInterface
} from '../../../../types/states'
import {
  getAvailabilitesFromSpesificDate,
  getAvailabilitesOnSpesificTime,
  getAvailabilitiesOnSpesificDate
} from '../../../../utils/applicantAvailabilities'
import { objectArrayContainsTime } from '../../../../utils/dates'
import { getShowingsTimesFromSpesificDate } from '../../../../utils/showings'
import { getFutureShowings } from '../../../../utils/showings/filterAndSearch'

const styles = ({ palette }: Theme) => ({
  container: {
    'box-sizing': 'border-box',
    background: '#E7ECEF',
    borderRadius: '27px',
    margin: 'auto',
    maxWidth: '450px',
    padding: '20px 15px 20px 15px'
  },
  heading: {
    textAlign: 'center' as const,
    padding: '.5rem 0rem',
    color: palette.grey[300]
  },
  timeButton: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '7px',
    padding: '0.5rem 0',
    cursor: 'pointer',
    background: '#ffffff',
    border: '2px solid #ffffff'
  },
  selectedTimeButton: {
    border: '2px solid #670033',
    background: '#670033',
    color: '#fff'
  },
  selectedExistingTimeButton: {
    border: '2px solid #670033',
    background: '#670033',
    color: '#fff'
  },
  existingTimeButton: {
    border: '2px solid #670033',
    background: '#670033',
    color: '#fff'
  },
  customTimeButton: {
    borderRadius: '4px',
    border: '1px solid #000000',
    textAlign: 'center' as const,
    padding: '8px',
    cursor: 'pointer',
    marginTop: '0.5rem'
  },
  timePicker: {
    'box-sizing': 'border-box',
    borderRadius: '7px',
    width: '100%'
  },
  submitCustomTimeButton: {
    borderRadius: '4px',
    textAlign: 'center' as const,
    padding: '8px 0',
    marginTop: '0.5rem',
    color: '#ffffff',
    background: palette.primary.main
  },
  error: {
    textAlign: 'center' as const,
    padding: '15px 0 15px 0',
    color: palette.error.main
  },
  suggestedBox: {
    position: 'absolute' as const,
    right: '-4px',
    top: '-4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: palette.primary.main,
    color: '#fff',
    height: '12px',
    width: '12px',
    borderRadius: '4px',
    border: '1px solid #fff'
  },
  suggestedBoxText: {
    fontSize: '0.6rem'
  },
  selectedTime: {
    background: '#f8fc9d',
    'pointer-events': 'none'
  }
})

interface TimePickTableProps {
  input: any
  meta: any
  changeDateTimeValue: any
  handleOnSubmit: any
  handleSubmit: any
  label: string
  custom: any
  submittingLoad: boolean
  activeDates?: string[]
  removeAvailability: any
}

interface StateProps {
  apartment?: Apartment
  existingShowings: Showing[]
  dateValueFromForm: Moment
  isDeleting: boolean
  availabilities?: ApplicantAvailability[]
}

interface DispatchProps {
  openDialog: OpenDialog
  closeDialog: CloseDialog
}

type ComponentProps = TimePickTableProps &
  WithTranslation &
  WithStyles &
  StateProps &
  DispatchProps

const SuggestTimePickTable = ({
  input,
  meta: { touched, invalid, error, active },
  changeDateTimeValue,
  classes,
  t,
  apartment,
  openDialog,
  closeDialog,
  handleOnSubmit,
  handleSubmit,
  removeAvailability,
  existingShowings,
  dateValueFromForm,
  activeDates,
  availabilities,
  submittingLoad,
  isDeleting
}: ComponentProps) => {
  const [selected, setSelected] = useState<string>('')
  const [suggestionRequired, setSuggestionRequired] = useState<boolean>(true)
  const [deleteSelecting, setDeleteSelecting] = useState<string>('')
  const dateTime = moment()
    .year(dateValueFromForm.year())
    .month(dateValueFromForm.month())
    .date(dateValueFromForm.date())
    .hours(8)
    .minutes(30)
    .second(0)
  const cellCount = 25
  const existingTimes = getShowingsTimesFromSpesificDate(
    existingShowings,
    dateValueFromForm
  )
  const existingAvailabilities = getAvailabilitesFromSpesificDate(
    availabilities ? availabilities : [],
    dateValueFromForm
  )
  const isExistingAvailablitiesTime = (time: Moment): boolean =>
    objectArrayContainsTime(existingAvailabilities, time)

  const removeDuplicatesFromArray = (timesArray: Moment[]): Moment[] => {
    const filteredArr = timesArray.reduce((acc: Moment[], current) => {
      const x = acc.find(
        (item) => item.format('H:mm') === current.format('H:mm')
      )
      if (!x) {
        return acc.concat([current])
      } else {
        return acc
      }
    }, [])
    return filteredArr
  }

  const getTimeArray = (): Moment[] => {
    const getTimeAsMinutes = (time: Moment) => {
      return time.hours() * 60 + time.minutes()
    }

    const array = []
    array[0] = dateTime

    /** Create basic timeArray with 30min separation */
    for (let i = 0; i < cellCount; i++) {
      let newTime: any
      if (i !== 0) {
        newTime = moment(dateTime).add(30 * i, 'minute')
      } else {
        newTime = dateTime
      }
      array[i] = newTime
    }
    const result = removeDuplicatesFromArray(array.concat(existingTimes)).sort(
      (a, b) => getTimeAsMinutes(a) - getTimeAsMinutes(b)
    )
    return result
  }

  const getClassForTime = (time: Moment): string => {
    if (isExistingAvailablitiesTime(time) && time.format('H:mm') === selected) {
      return classes.selectedExistingTimeButton
    } else if (isExistingAvailablitiesTime(time)) {
      return classes.existingTimeButton
    } else if (time.format('H:mm') === selected) {
      return classes.selectedTimeButton
    } else {
      return classes.timeButton
    }
  }

  const handleOnChange = (time: Moment): void => {
    if (!availabilities) {
      return
    }
    const availabilitiesOnSelectedDate = getAvailabilitiesOnSpesificDate(
      availabilities,
      dateValueFromForm
    )
    const availabilitiesOnSelectedTime = getAvailabilitesOnSpesificTime(
      availabilitiesOnSelectedDate,
      time
    )
    if (
      availabilitiesOnSelectedDate &&
      availabilitiesOnSelectedDate.length !== 0
    ) {
      availabilitiesOnSelectedDate.map((availability) => {
        if (
          moment(availability.startTime).format('H:mm') ===
            time.format('H:mm') &&
          availabilitiesOnSelectedTime
        ) {
          removeAvailability(availability.applicantAvailabilityId)
          setSelected('')
          setDeleteSelecting(time.format('H:mm'))
          changeDateTimeValue('time', {})
        }
      })
      if (availabilitiesOnSelectedTime.length === 0) {
        setSelected(time.format('H:mm'))
        changeDateTimeValue('time', {
          startTime: time,
          endTime: moment(time).add(30, 'minutes')
        })
        setTimeout(handleSubmit(handleOnSubmit))
      }
    } else {
      setSelected(time.format('H:mm'))
      changeDateTimeValue('time', {
        startTime: time,
        endTime: moment(time).add(30, 'minutes')
      })
      setTimeout(handleSubmit(handleOnSubmit))
    }
  }

  const handleGetIsSelectedDate = (time: Moment) => {
    if (
      Array.isArray(activeDates) &&
      activeDates.length > 0 &&
      dateValueFromForm
    ) {
      const result = activeDates.some(
        (element) =>
          `${dateValueFromForm.format('DD')}-${time.format('HH:mm')}` ===
          moment(element).format('DD-HH:mm')
      )
      return result ? classes.selectedTime : ''
    }
    return ''
  }
  useEffect(() => {
    if (Array.isArray(availabilities) && availabilities.length > 0) {
      if (availabilities.length === 1 && suggestionRequired) {
        toast.info(i18n.t('showings.createShowings.messages.suggestions'), {
          autoClose: 10000
        })
        setSuggestionRequired(false)
      }
    } else {
      if (!suggestionRequired) {
        setSuggestionRequired(true)
      }
    }
  }, [availabilities])
  useEffect(() => {
    if (Array.isArray(activeDates)) {
      if (
        activeDates.some(
          (element) => selected === moment(element).format('HH:mm')
        )
      ) {
        setSelected('')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDates])
  useEffect(() => {
    setSelected('')
  }, [dateValueFromForm])
  return (
    <Fragment>
      <div className={classes.container}>
        <Grid item xs={12} sm={12}>
          {invalid && touched && !active && (
            <Typography className={classes.error} variant='body1'>
              {error}
            </Typography>
          )}
        </Grid>
        <Grid container spacing={1}>
          {getTimeArray().map((time, index) => {
            return (
              <Grid key={index} item style={{ width: '20%' }}>
                {time && (
                  <div
                    className={`${classes.timeButton} ${getClassForTime(
                      time
                    )} ${handleGetIsSelectedDate(time)}`}
                    onClick={() => {
                      handleOnChange(time)
                    }}
                  >
                    <Typography variant='button'>
                      {(submittingLoad && time.format('H:mm') === selected) ||
                      (isDeleting &&
                        time.format('H:mm') === deleteSelecting) ? (
                        <Grid container>
                          <CircularProgress
                            size={24.5}
                            style={{
                              color: '#fff'
                            }}
                          />
                        </Grid>
                      ) : (
                        <> {time.format('H:mm')}</>
                      )}
                    </Typography>
                  </div>
                )}
              </Grid>
            )
          })}
        </Grid>
      </div>
    </Fragment>
  )
}

const mapStateToProps = (state: StateInterface) => {
  const apartmentId = selectContentIdFromPathname(state)
  const showingsInterface = apartmentId
    ? selectShowingsInterfaceByApartmentId(apartmentId)(state)
    : EmptyShowingsInterface
  const dateValueFromForm = formValueSelector('SuggestShowingTimesForm')(
    state,
    'dateRange.startDate'
  )
  return {
    apartment: apartmentId
      ? selectApartmentById(apartmentId)(state)
      : undefined,
    existingShowings: getFutureShowings(showingsInterface.showings, 'reserve'),
    dateValueFromForm,
    availabilities: selectAvailabilitiesByApplicant(state),
    isDeleting: selectIsAvailabilityDeleting(state)
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  openDialog: (id: string) => dispatch(openDialog(id)),
  closeDialog: (id: string) => dispatch(closeDialog(id))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withStyles(styles)(SuggestTimePickTable)))
